/**
 * TimeloomRenderer - React component that replaces BBC VideoContext with Timeloom
 *
 * ✅ FIXED: This component now follows the correct pattern (no private API calls)
 *
 * **Architecture:**
 * This component creates an EMPTY timeline container. Scenes are NOT added here.
 * Scenes are added inline in SequencerRendererImpl._scheduleAudioNodeToContext()
 * (lines 759-822) during playback, using merged nodes created internally.
 *
 * **Pattern Followed:**
 * Same as VideoContext - the renderer provides an empty container, and
 * SequencerRendererImpl populates it during audio scheduling. No converter needed.
 *
 * **See:** CLAUDE.md "Private Method Usage Pattern" for the detailed pattern explanation.
 *
 * This component provides a modern, professional video rendering system for Trebble's
 * audio/video editor, enabling advanced transitions, multi-layer effects, and overlay
 * support that surpasses the capabilities of the legacy VideoContext library.
 *
 * Key Features:
 * - Creates empty Timeline container (scenes added during playback)
 * - Manages Remotion Player lifecycle
 * - Synchronizes video playback with WebAudio API
 * - Handles dynamic video resolution
 *
 * Integration Points:
 * - SequencerView.js: initTimeloomRenderer()
 * - Sequencer.js: getTimeloomRenderer(), setTimeloomRenderer()
 * - SequencerRendererImpl.js: Adds scenes inline (lines 759-822)
 *
 * Feature Flag: USE_TIMELOOM_RENDERER (webpack DefinePlugin)
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Player, PlayerRef } from '@remotion/player';
import { TimelineImpl } from '../timeloom/Timeline';
import { ensureTimelineDuration } from '../timeloom/helpers/TimelineDurationHelper';
import { VideoRenderer, VideoRendererType } from '../../models/audioEditor/interfaces/VideoRenderer';
import { AspectRatio, detectResolutionTier } from '../timeloom/AspectRatio';
import type { TimeloomSequencerLike } from '../../models/audioEditor/types/SequencerRenderingTypes';
import { DEFAULT_THEME_CONFIG } from './TimeloomDefaultThemeConfig';

/**
 * Calculate aspect ratio from video dimensions
 * Same logic as TimeloomRenderingInputBuilder
 */
function calculateAspectRatio(width: number, height: number): AspectRatio {
    const ratio = width / height;
    const tolerance = 0.02;

    // 16:9 landscape (1.777...)
    if (Math.abs(ratio - 16/9) < tolerance) {
        return '16:9';
    }
    // 9:16 portrait (0.5625)
    if (Math.abs(ratio - 9/16) < tolerance) {
        return '9:16';
    }
    // 1:1 square
    if (Math.abs(ratio - 1) < tolerance) {
        return '1:1';
    }
    // 4:5 (0.8)
    if (Math.abs(ratio - 4/5) < tolerance) {
        return '4:5';
    }

    // Default to 16:9 for unrecognized ratios
    return '16:9';
}

type I18nModule = Record<string, unknown>;

interface TimeloomRendererI18nGlobal {
    getI18n?: (i18nData: I18nModule, key: string, options?: Record<string, unknown>) => string;
}

/**
 * Public API exposed to Backbone views
 * Implements VideoRenderer interface for compatibility with Sequencer system
 *
 * Provides control interface for video playback and timeline management
 */
export interface ModernVideoAPI extends VideoRenderer {
    /**
     * Explicit renderer type for type-safe detection
     * Replaces brittle duck-typing with explicit type property
     */
    readonly rendererType: VideoRendererType.Timeloom;
    /**
     * Start video playback from current position
     */
    play(): void;

    /**
     * Pause video playback
     */
    pause(): void;

    /**
     * Seek to specific time in milliseconds
     * @param timeMs - Target time in milliseconds
     */
    seek(timeMs: number): void;

    /**
     * Get current playback time in milliseconds
     */
    getCurrentTime(): number;

    /**
     * Get total duration in milliseconds
     */
    getDuration(): number;

    /**
     * Get underlying Timeloom timeline instance
     */
    getTimeline(): TimelineImpl;

    /**
     * Clear all scenes from timeline (for incremental scene addition pattern)
     */
    clearScenes(): void;

    /**
     * Clean up resources and destroy renderer
     */
    destroy(): void;

    /**
     * Update timeline from sequencer changes
     * Now async to await SequencerRendererHelper scheduling info
     */
    refresh(): Promise<void>;

    /**
     * Wait for video to be ready after seeking
     * This ensures video has buffered and decoded before playback starts
     * Critical for sync with audio clock
     * @param timeoutMs - Maximum time to wait in milliseconds (default: 5000)
     * @returns Promise that resolves when video is ready or rejects on timeout
     */
    waitForVideoReady(timeoutMs?: number): Promise<void>;

    /**
     * Internal player reference for event listeners
     * @internal - Used for advanced sync features
     */
    playerRef?: React.RefObject<PlayerRef | null>;
}

/**
 * Props interface for TimeloomRenderer component
 */
export interface TimeloomRendererProps {
    /**
     * Backbone Sequencer model containing video/audio data
     */
    sequencer: TimeloomSequencerLike;

    /**
     * Canvas width in pixels
     * @default 268
     */
    width?: number;

    /**
     * Canvas height in pixels
     * @default 150
     */
    height?: number;

    /**
     * Callback when renderer is ready
     * Provides API for controlling playback
     */
    onReady?: (api: ModernVideoAPI) => void;

    /**
     * Callback when error occurs
     */
    onError?: (error: Error) => void;

    /**
     * Callback when playback time changes
     * @param timeMs - Current time in milliseconds
     */
    onTimeUpdate?: (timeMs: number) => void;

    /**
     * Whether to enable debug logging
     * @default false
     */
    debug?: boolean;

    /**
     * i18n module for localized strings
     * Optional - falls back to English if not provided
     */
    i18nModule?: I18nModule;
}

/**
 * TimeloomRenderer Component
 *
 * Renders video timeline using Timeloom/Remotion instead of VideoContext.
 * Provides professional video editing capabilities with transitions, effects, and overlays.
 */
export const TimeloomRenderer: React.FC<TimeloomRendererProps> = ({
    sequencer,
    width = 268,
    height = 150,
    onReady,
    onError,
    onTimeUpdate,
    debug = false,
    i18nModule
}) => {
    // Refs
    const playerRef = useRef<PlayerRef>(null);
    const apiRef = useRef<ModernVideoAPI | null>(null);
    const pendingWaitsRef = useRef<Set<() => void>>(new Set());

    // State
    const [timeline, setTimeline] = useState<TimelineImpl | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [, setTimelineVersion] = useState(0); // Force re-render when timeline changes
    const [mountedPlayer, setMountedPlayer] = useState<PlayerRef | null>(null);

    const handlePlayerRef = useCallback((player: PlayerRef | null) => {
        playerRef.current = player;
        setMountedPlayer(player);
    }, []);

    /**
     * Log debug messages when debug mode is enabled
     */
    const log = useCallback((...args: unknown[]) => {
        if (debug) {
            console.log('[TimeloomRenderer]', ...args);
        }
    }, [debug]);

    /**
     * i18n helper function for translating UI strings
     * Falls back to English if i18nModule is not provided or window.getI18n is unavailable
     */
    const t = useCallback((key: string, fallback: string) => {
        const trebbleWindow = typeof window !== 'undefined'
            ? window as TimeloomRendererI18nGlobal
            : undefined;
        if (i18nModule && typeof trebbleWindow?.getI18n === 'function') {
            return trebbleWindow.getI18n(i18nModule, key);
        }
        return fallback;
    }, [i18nModule]);

    /**
     * CRITICAL: Memoize TimelineComposition BEFORE any conditional returns
     * This ensures the hook is always called in the same order (React Rules of Hooks)
     * Must be declared here at the top level, after all other hooks but before any early returns
     */
    const TimelineComposition = useMemo<React.FC>(() => {
        // Only render timeline if it exists, otherwise return empty fragment
        if (!timeline) {
            const EmptyTimelineComposition: React.FC = () => <></>;
            return EmptyTimelineComposition;
        }
        const RenderedTimelineComposition: React.FC = () => <>{timeline.render()}</>;
        return RenderedTimelineComposition;
    }, [timeline]);

    /**
     * Initialize EMPTY timeline container
     *
     * IMPORTANT: This creates an EMPTY timeline. Scenes are NOT added here.
     * Scenes are added inline during _scheduleAudioNodeToContext in SequencerRendererImpl.js
     * (lines 759-822) when playback is triggered.
     *
     * This follows the same pattern as VideoContext:
     * 1. Renderer creates empty container
     * 2. SequencerRendererImpl adds scenes during scheduling
     * 3. No converter needed - scenes added directly from merged nodes
     */
    useEffect(() => {
        log('Initializing TimeloomRenderer', { sequencer, width, height });

        try {
            // Get video configuration from sequencer
            const settings = sequencer.getSequencerSettings?.();
            const rawResolution = settings?.getVideoResolution?.();
            const resolution = {
                width: (typeof rawResolution?.width === 'number' && rawResolution.width > 0) ? rawResolution.width : 1920,
                height: (typeof rawResolution?.height === 'number' && rawResolution.height > 0) ? rawResolution.height : 1080
            };
            const fps = settings?.getVideoFps?.() ?? 30;

            // ✅ FIX: Calculate aspect ratio from actual resolution instead of hardcoding
            const aspectRatio = calculateAspectRatio(resolution.width, resolution.height);

            // ✅ NEW: Detect resolution tier from sequencer's video resolution
            const resolutionTier = detectResolutionTier(resolution.width, resolution.height);

            // Get sequencer total duration for initial timeline duration
            // This ensures Player has valid duration even before scenes are added
            const sequencerDurationMs = sequencer.getTotalDuration?.() ?? 0;
            const sequencerDurationSeconds = sequencerDurationMs / 1000;

            log('Creating timeline with resolution:', resolution, 'fps:', fps, 'aspectRatio:', aspectRatio, 'resolutionTier:', resolutionTier, 'duration:', sequencerDurationSeconds + 's');

            // Create timeline container with sequencer's duration
            // Scenes will be added inline by SequencerRendererImpl during playback
            const emptyTimeline = new TimelineImpl({
                fps: fps,
                theme: DEFAULT_THEME_CONFIG,
                defaults: {
                    layoutPadding: 0,
                    transitionIn: { type: 'fade', duration: 0, mode: 'prefix' },
                    transitionOut: { type: 'fade', duration: 0, mode: 'postfix' }
                },
                aspectRatio: aspectRatio,
                resolutionTier: resolutionTier,  // ✅ NEW: Pass detected resolution tier
                videoConfig: {
                    width: resolution.width,
                    height: resolution.height
                }
            });

            // Set timeline duration from sequencer
            // Player needs this upfront even though scenes are added later
            emptyTimeline.durationInSeconds = sequencerDurationSeconds;

            log('Timeline created with duration:', sequencerDurationSeconds + 's', '- scenes will be added during playback');
            setTimeline(emptyTimeline);
            setIsLoading(false);

        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            console.error('[TimeloomRenderer] Initialization error:', error);
            setError(error);
            setIsLoading(false);
            onError?.(error);
        }
    }, [sequencer, debug, log, onError, width, height]);

    /**
     * Create and expose API when timeline is ready
     */
    useEffect(() => {
        log('API useEffect triggered - timeline:', timeline, 'mountedPlayer:', mountedPlayer);

        if (!timeline || !mountedPlayer) {
            log('API useEffect early return - timeline exists:', !!timeline, 'player exists:', !!mountedPlayer);
            return;
        }

        log('API useEffect proceeding - both timeline and player exist');
        const player = mountedPlayer;

        // Create API object implementing VideoRenderer interface
        const api: ModernVideoAPI = {
            // Explicit renderer type for type-safe detection
            rendererType: VideoRendererType.Timeloom,

            play: () => {
                log('API.play() called');
                log('playerRef.current:', player);
                player.play();
                log('player.play() executed');
            },

            pause: () => {
                log('API.pause() called');
                player.pause();
            },

            seek: (timeMs: number) => {
                log('API.seek() called with timeMs:', timeMs);
                const frame = Math.round((timeMs / 1000) * timeline.settings.fps);
                player.seekTo(frame);
            },

            getCurrentTime: () => {
                const frame = player.getCurrentFrame();
                return Math.round((frame / timeline.settings.fps) * 1000);
            },

            getDuration: () => {
                return Math.round(timeline.getDuration() * 1000);
            },

            getTimeline: () => timeline,

            clearScenes: () => {
                if (timeline) {
                    timeline.clearScenes();
                }
            },

            refresh: async () => {
                log('API: refresh()');
                // Force re-render after scenes are added inline
                // This updates the Player's durationInFrames when timeline duration changes
                log('Forcing re-render to update Player duration after scenes added');
                setTimelineVersion(v => v + 1);
            },

            destroy: () => {
                log('API: destroy()');
                player.pause();

                // Clean up all pending waitForVideoReady promises
                pendingWaitsRef.current.forEach(cleanup => cleanup());
                pendingWaitsRef.current.clear();

                setTimeline(null);
            },

            waitForVideoReady: (timeoutMs: number = 5000) => {
                return new Promise<void>((resolve, reject) => {
                    const startTime = performance.now();
                    log('waitForVideoReady called');
                    log('  - Timeout:', timeoutMs, 'ms');
                    log('  - Current time:', new Date().toISOString());
                    log('  - Player ready state:', player);

                    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
                    let seekedListener: ((event: { detail: { frame: number } }) => void) | null = null;

                    // Flag to prevent double settlement
                    let settled = false;

                    // Cleanup function
                    const cleanup = () => {
                        // Idempotent - return early if already settled
                        if (settled) return;
                        settled = true;

                        if (timeoutHandle) {
                            clearTimeout(timeoutHandle);
                            timeoutHandle = null;
                        }
                        if (seekedListener && player) {
                            player.removeEventListener('seeked', seekedListener);
                            seekedListener = null;
                        }
                        // Remove from pending waits set
                        pendingWaitsRef.current.delete(cleanup);
                    };

                    // Register cleanup for component unmount
                    pendingWaitsRef.current.add(cleanup);

                    // Set up timeout
                    timeoutHandle = setTimeout(() => {
                        const elapsedTime = performance.now() - startTime;
                        cleanup();
                        const error = new Error(`Video ready timeout after ${timeoutMs}ms (actual: ${elapsedTime}ms)`);
                        console.warn('[TimeloomRenderer] waitForVideoReady TIMEOUT:');
                        console.warn('  - Expected timeout:', timeoutMs, 'ms');
                        console.warn('  - Actual elapsed:', elapsedTime, 'ms');
                        console.warn('  - seeked event never fired');
                        reject(error);
                    }, timeoutMs);

                    // Listen for seeked event (fired when seek completes)
                    seekedListener = event => {
                        const elapsedTime = performance.now() - startTime;
                        log('seeked EVENT FIRED');
                        log('  - Elapsed time:', elapsedTime, 'ms');
                        log('  - Event:', event);
                        cleanup();
                        resolve();
                    };

                    log('Adding seeked event listener');
                    player.addEventListener('seeked', seekedListener);
                    log('Event listener added, waiting for seeked event...');
                });
            },

            // Expose playerRef for advanced sync features
            playerRef: playerRef
        };

        apiRef.current = api;

        // Notify parent that API is ready
        log('API created and ready:', api);
        log('Calling onReady callback');
        onReady?.(api);
        log('onReady callback completed');

    }, [timeline, sequencer, log, onReady, onError, mountedPlayer]);

    /**
     * Handle playback time updates
     */
    const handleFrame = useCallback((frame: number) => {
        if (!timeline || !onTimeUpdate) return;

        const timeMs = Math.round((frame / timeline.settings.fps) * 1000);
        onTimeUpdate(timeMs);
    }, [timeline, onTimeUpdate]);

    useEffect(() => {
        if (!mountedPlayer) {
            return;
        }
        const player = mountedPlayer;

        const frameUpdateListener = (event: { detail: { frame: number } }) => {
            handleFrame(event.detail.frame);
        };

        player.addEventListener('frameupdate', frameUpdateListener);
        return () => {
            player.removeEventListener('frameupdate', frameUpdateListener);
        };
    }, [handleFrame, mountedPlayer]);

    /**
     * Cleanup on component unmount
     * Ensures all pending promises and event listeners are cleaned up
     */
    useEffect(() => {
        return () => {
            log('Component unmounting, cleaning up pending operations');
            // Clean up all pending waitForVideoReady promises
            pendingWaitsRef.current.forEach(cleanup => cleanup());
            pendingWaitsRef.current.clear();
        };
    }, [log]);

    // Loading state
    if (isLoading) {
        return (
            <div
                className="timeloom-renderer-loading"
                style={{
                    width,
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#000',
                    color: '#fff'
                }}
            >
                {t('TIMELOOM_LOADING', 'Loading video renderer...')}
            </div>
        );
    }

    // Error state
    if (error || !timeline) {
        return (
            <div
                className="timeloom-renderer-error"
                style={{
                    width,
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#000',
                    color: '#f00',
                    padding: '10px',
                    textAlign: 'center'
                }}
            >
                <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                        {t('TIMELOOM_ERROR_TITLE', 'Video Renderer Error')}
                    </div>
                    <div style={{ fontSize: '12px' }}>
                        {error?.message || t('TIMELOOM_INIT_FAILED', 'Failed to initialize Timeloom renderer')}
                    </div>
                </div>
            </div>
        );
    }

    // Ensure duration is calculated before checking it
    // The duration calculation happens in timeline.render(), but we need it now
    // So we manually trigger the calculation before checking
    ensureTimelineDuration(timeline);

    // Calculate video configuration
    const duration = timeline.getDuration();
    log('Render phase - timeline.getDuration():', duration);

    const videoConfig = {
        id: `sequencer-${sequencer.getId?.() || 'video'}`,
        width,
        height,
        fps: timeline.settings.fps,
        durationInFrames: Math.ceil(duration * timeline.settings.fps)
    };

    log('Render phase - videoConfig:', videoConfig);

    // Empty timeline state - no video content yet
    if (duration === 0 || videoConfig.durationInFrames === 0) {
        log('Render phase - EMPTY TIMELINE - returning early (duration:', duration, 'durationInFrames:', videoConfig.durationInFrames, ')');
        return (
            <div
                className="timeloom-renderer-empty"
                style={{
                    width,
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#000',
                    color: '#888',
                    fontSize: '12px'
                }}
            >
                {t('TIMELOOM_NO_CONTENT', 'No video content')}
            </div>
        );
    }

    log('Render phase - Rendering Remotion Player with durationInFrames:', videoConfig.durationInFrames);

    // ✅ FIX: Use actual video resolution from timeline for composition dimensions
    // The canvas dimensions (width/height props) are just for display,
    // but the composition must use the actual video resolution to prevent distortion
    const actualVideoWidth = typeof timeline.videoConfig.width === 'number' && timeline.videoConfig.width > 0 ? timeline.videoConfig.width : 1920;
    const actualVideoHeight = typeof timeline.videoConfig.height === 'number' && timeline.videoConfig.height > 0 ? timeline.videoConfig.height : 1080;

    log('Render - Canvas dimensions:', { width, height });
    log('Render - Actual video resolution:', { actualVideoWidth, actualVideoHeight });

    // Render Remotion Player with Timeloom timeline
    return (
        <div
            className="timeloom-renderer-container"
            style={{
                width,
                height,
                position: 'relative',
                backgroundColor: '#000'
            }}
        >
            <Player
                ref={handlePlayerRef}
                component={TimelineComposition}
                inputProps={{}}
                durationInFrames={videoConfig.durationInFrames}
                compositionWidth={actualVideoWidth}
                compositionHeight={actualVideoHeight}
                fps={videoConfig.fps}
                style={{
                    width: '100%',
                    height: '100%'
                }}
                controls={false}
                loop={false}
                clickToPlay={false}
                doubleClickToFullscreen={false}
            />
        </div>
    );
};

/**
 * Default export for easier imports
 */
export default TimeloomRenderer;
